import { Box, Typography, Button } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { customBlue } from "../../theme/palette";

interface FinalMicCheckStateProps {
  onStart?: () => void;
  onBackToDashboard?: () => void;
  onListenToWaitInstructions?: () => void;
}

export function FinalMicCheckState({
  onStart,
  onBackToDashboard,
  onListenToWaitInstructions,
}: FinalMicCheckStateProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        height: "100vh",
        textAlign: "center",
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 4 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
        }}
      >
        <Button
          variant="text"
          onClick={onListenToWaitInstructions}
          startIcon={
            <Box
              sx={{
                border: `2px solid ${customBlue.primaryBlue}`,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                backgroundColor: "transparent",
              }}
            >
              <VolumeUpIcon
                sx={{
                  fontSize: "14px",
                  width: "14px",
                  height: "14px",
                  color: customBlue.primaryBlue,
                }}
              />
            </Box>
          }
          sx={{
            color: customBlue.primaryBlue,
            fontSize: "16px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          Listen to Instructions
        </Button>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "#FFA726",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
            fontSize: "18px",
          }}
        >
          ?
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          pt: { xs: 8, sm: 10 },
          pb: { xs: 4, sm: 6 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "32px", sm: "40px", md: "48px" },
            fontWeight: "bold",
            color: "#1e40af",
            mb: 2,
            fontFamily: "Nunito",
          }}
        >
          Please Wait!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "16px", sm: "18px" },
            color: "#374151",
            mb: { xs: 2, sm: 4 },
            lineHeight: 1.6,
            maxWidth: "500px",
            fontFamily: "Nunito",
            fontWeight: 700,
          }}
        >
          Your teacher will tell you to
          <br />
          start when everyone is ready.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: { xs: 2, sm: 4 },
          }}
        >
          <Box
            component="img"
            src="/images/im-ready.png"
            alt="I'm ready"
            sx={{
              width: { xs: 600, sm: 800, md: 1086 },
              height: { xs: 600, sm: 800, md: 1086 },
              objectFit: "contain",
              maxWidth: "90vw",
              maxHeight: "60vh",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          px: { xs: 2, sm: 4 },
          pb: { xs: 2, sm: 4 },
          gap: { xs: 2, sm: 4 },
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "stretch" },
        }}
      >
        <Button
          onClick={onBackToDashboard}
          variant="outlined"
          sx={{
            minWidth: { xs: "200px", sm: "180px" },
            width: { xs: "100%", sm: "auto" },
            borderColor: customBlue.primaryBlue,
            color: customBlue.primaryBlue,
            textTransform: "uppercase",
            fontWeight: 700,
            py: { xs: 1.5, sm: 1 },
            "&:hover": {
              backgroundColor: "rgba(99, 102, 241, 0.04)",
              borderColor: customBlue.primaryDark,
            },
          }}
        >
          back to dashboard
        </Button>
        <Button
          onClick={onStart}
          variant="contained"
          sx={{
            minWidth: { xs: "200px", sm: "180px" },
            width: { xs: "100%", sm: "auto" },
            backgroundColor: customBlue.primaryBlue,
            textTransform: "uppercase",
            fontWeight: 700,
            height: "48px",
            "&:hover": {
              backgroundColor: customBlue.primaryDark,
            },
          }}
        >
          start
        </Button>
      </Box>
    </Box>
  );
}
